import jwt_decode from 'jwt-decode';
import AuthApi from '../API/auth/Auth.api';
import { TokenData } from './token-data';

const JWT_TOKEN = 'jwt-token';
const LANG_TOKEN = 'i18nextLng';
const REFRESH_TOKEN = 'refresh-token';
const IS_PASSWORD_CHANGED = 'is-password-changed';

const localStorageService = {
  setPasswordChanged: (value: boolean) => {
    localStorage.setItem(IS_PASSWORD_CHANGED, value.toString());
  },
  setRefreshToken: (token: string) => {
    if (token) {
      localStorage.setItem(REFRESH_TOKEN, token);
    }
  },
  setJWTToken: (token: string) => {
    if (token) {
      localStorage.setItem(JWT_TOKEN, token);
    }
  },
  setTokens: (refreshToken: string, JWTToken: string) => {
    localStorageService.setRefreshToken(refreshToken);
    localStorageService.setJWTToken(JWTToken);
  },
  getIsPasswordChange: () => {
    const isPasswordChanged = localStorage.getItem(IS_PASSWORD_CHANGED);
    return Boolean(isPasswordChanged);
  },
  getCurrentJWT: (): string | null => {
    return localStorage.getItem(JWT_TOKEN);
  },
  getJWTToken: async (current?: boolean): Promise<string | null> => {
    const jwtToken = localStorage.getItem(JWT_TOKEN);
    if (current) {
      return jwtToken;
    }

    //if access token invalid
    if (!jwtToken || !localStorageService.isTokenValid(jwtToken)) {
      localStorageService.removeAllTokens();
      return null;
    }

    //if access token expired
    if (localStorageService.isTokenExpired(jwtToken)) {
      const refreshToken = localStorageService.getRefreshToken();
      if (!refreshToken) {
        return null;
      }

      const jwtTokenData = localStorageService.getDataFromToken(jwtToken);

      try {
        const response = await AuthApi.refreshToken({
          refreshToken,
          email: jwtTokenData?.email,
        });
        localStorageService.setTokens(
          response.data.tokens.refreshToken,
          response.data.tokens.jwtToken
        );

        return response.data.tokens.jwtToken;
      } catch (error) {
        console.log(`Refresh token failed, ${JSON.stringify(error)}`);
        localStorageService.removeAllTokens();
      }
    }
    return jwtToken;
  },
  getRefreshToken: (): string | null => {
    return localStorage.getItem(REFRESH_TOKEN);
  },
  getLangToken: (): string | null => {
    return localStorage.getItem(LANG_TOKEN);
  },
  removeRefreshToken: () => {
    localStorage.removeItem(REFRESH_TOKEN);
  },
  removeJWTToken: () => {
    localStorage.removeItem(JWT_TOKEN);
  },
  removeAllTokens: () => {
    localStorageService.removeRefreshToken();
    localStorageService.removeJWTToken();
  },
  isTokenValid: (token: string): boolean => {
    try {
      return !!jwt_decode(token);
    } catch (error) {
      return false;
    }
  },
  isTokenExpired: (token: string): boolean => {
    try {
      const gapTime = 1000 * 60 * 60; //1hr in ms
      const expTime = (jwt_decode(token) as any).exp * 1000;
      const dateNow = new Date().getTime() + gapTime;
      return dateNow >= expTime;
    } catch (error) {
      return true;
    }
  },

  getDataFromToken: (token: string | null): TokenData | undefined => {
    if (!token) {
      return undefined;
    }
    if (localStorageService.isTokenValid(token)) {
      return jwt_decode(token);
    } else {
      return undefined;
    }
  },

  getUserEmailFromToken: async (): Promise<string | undefined> => {
    const jwtToken = await localStorageService.getJWTToken();
    if (!jwtToken) {
      return undefined;
    }
    const data = localStorageService.getDataFromToken(jwtToken);
    return data?.email;
  },

  getUserPhoneFromToken: async (): Promise<string | undefined> => {
    const jwtToken = await localStorageService.getJWTToken();
    if (!jwtToken) {
      return undefined;
    }
    const data = localStorageService.getDataFromToken(jwtToken);
    return data?.phone_number;
  },
};

export default localStorageService;
